/* Self-contained illustrations, drawn as SVG data URIs so designs need no image files. */
import type { Colors } from '../model/types';
import { hexToRgb, isDark, mix, rgbToHsl, sat0 } from '../lib/color';

const svgURI = (svg: string) => 'data:image/svg+xml,' + encodeURIComponent(svg);

/* ---------- sample landscapes shown until the couple adds photos ---------- */
const _ph = new Map<string, string>();
export function placeholderURI(i: number, c: Colors) {
  const key = (i % 6) + c.bg + c.accent + c.ink;
  const hit = _ph.get(key);
  if (hit) return hit;
  const dark = isDark(c.bg);
  const base = dark ? mix(c.bg, '#ffffff', 0.12) : c.bg;
  const skyT = mix(base, sat0(c.accent) < 0.18 ? '#C9D6DA' : c.accent, 0.3), skyB = mix(base, dark ? '#9aa4b8' : '#ffffff', 0.35);
  const [ar, ag, ab] = hexToRgb(c.accent);
  const sat = (Math.max(ar, ag, ab) - Math.min(ar, ag, ab)) / (Math.max(ar, ag, ab) || 1);
  const tone = sat < 0.18 ? (dark ? '#9FB396' : '#8EA487') : c.accent;
  const far = mix(tone, base, 0.6), mid = mix(tone, base, 0.36), near = mix(tone, c.ink, 0.22);
  const sun = mix(base, '#F2C6A0', 0.7);
  const scenes = [
    `<circle cx="292" cy="112" r="34" fill="${sun}"/><path d="M0,190 C80,150 150,170 220,160 C300,148 350,170 400,160 V300 H0Z" fill="${far}"/><path d="M0,230 C90,200 170,232 260,210 C320,196 360,214 400,206 V300 H0Z" fill="${mid}"/><path d="M0,262 C110,246 220,276 400,252 V300 H0Z" fill="${near}"/>`,
    `<circle cx="120" cy="150" r="28" fill="${sun}"/><rect y="176" width="400" height="124" fill="${mid}"/><path d="M60,196 H180 M90,214 H150 M100,232 H140" stroke="${sun}" stroke-width="2" opacity=".6"/><path d="M262,170 L262,128 L290,170Z M250,172 L300,172 L292,180 L258,180Z" fill="${near}"/>`,
    `<path d="M0,300 V220 L30,170 L60,220 L80,150 L110,220 L140,170 L170,220 L200,140 L230,220 L260,166 L290,220 L320,150 L350,220 L380,176 L400,206 V300Z" fill="${far}"/><path d="M0,300 V246 L40,196 L80,246 L120,206 L160,246 L210,190 L250,246 L300,200 L340,246 L380,210 L400,236 V300Z" fill="${near}"/>`,
    `<rect y="214" width="400" height="86" fill="${mid}"/><path d="M0,214 C120,204 280,224 400,212" stroke="${near}" stroke-width="2" fill="none"/><rect x="236" y="150" width="8" height="70" fill="${near}"/><circle cx="240" cy="132" r="44" fill="${near}"/><circle cx="214" cy="146" r="28" fill="${mid}" opacity=".7"/><circle cx="96" cy="88" r="22" fill="${sun}"/>`,
    `<path d="M0,250 L90,120 L150,200 L220,96 L310,220 L350,170 L400,230 V300 H0Z" fill="${mid}"/><path d="M90,120 L108,146 L96,142 L84,150 L72,146Z M220,96 L242,128 L226,122 L212,132 L198,126Z" fill="${base}" opacity=".9"/><path d="M0,270 C120,250 260,284 400,262 V300 H0Z" fill="${near}"/>`,
    `<rect y="200" width="400" height="100" fill="${mid}"/>${Array.from({ length: 26 }, (_, k) => { const x = 12 + k * 15 + (k % 3) * 3, y = 214 + ((k * 37) % 70); return `<path d="M${x},${y + 30} V${y}" stroke="${near}" stroke-width="1.5"/><circle cx="${x}" cy="${y}" r="${3 + (k % 3)}" fill="${k % 2 ? sun : mix(tone, base, 0.15)}"/>`; }).join('')}<circle cx="300" cy="90" r="26" fill="${sun}"/>`,
  ];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice"><defs><linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${skyT}"/><stop offset="1" stop-color="${skyB}"/></linearGradient></defs><rect width="400" height="300" fill="url(#s)"/>${scenes[i % 6]}</svg>`;
  const uri = svgURI(svg);
  _ph.set(key, uri);
  return uri;
}

/* ---------- paper texture ---------- */
const _noise = new Map<boolean, string>();
export function noiseCSS(dark: boolean) {
  const hit = _noise.get(dark);
  if (hit) return hit;
  const c = dark ? 1 : 0;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency=".8" numOctaves="3" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 ${c} 0 0 0 0 ${c} 0 0 0 0 ${c} 0 0 0 ${dark ? 0.09 : 0.07} 0"/></filter><filter id="m"><feTurbulence type="fractalNoise" baseFrequency=".55" numOctaves="1" seed="9" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 ${c} 0 0 0 0 ${c} 0 0 0 0 ${c} 3.2 0 0 0 -2.1"/></filter><rect width="100%" height="100%" filter="url(#n)"/><rect width="100%" height="100%" filter="url(#m)" opacity=".22"/></svg>`;
  const css = `url("${svgURI(svg)}")`;
  _noise.set(dark, css);
  return css;
}

/* ---------- Wreath design ---------- */
const _wreath = new Map<string, string>();
export function wreathURI(c: Colors) {
  const key = c.accent + c.bg;
  const hit = _wreath.get(key);
  if (hit) return hit;
  let seed = 11;
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const [, sat] = rgbToHsl(hexToRgb(c.accent));
  const flower = sat > 0.2 ? c.accent : '#E3A49B';
  const flower2 = mix(flower, '#ffffff', 0.5);
  const cx = 300, cy = 300, R = 228;
  const greens = ['#7E9A76', '#9DB394', '#6A8763', '#B3C6A9'];
  let leaves = '';
  const N = 64;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + rnd() * 0.04;
    const deg = (a * 180) / Math.PI;
    const side = i % 2 ? 1 : -1;
    const r = R + side * (8 + rnd() * 6);
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    const len = 20 + rnd() * 12, w = 7 + rnd() * 3;
    const rot = deg + 90 + side * (32 + rnd() * 14);
    leaves += `<ellipse cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="${len.toFixed(1)}" ry="${w.toFixed(1)}" transform="rotate(${rot.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})" fill="${greens[i % 4]}" opacity=".95"/>`;
  }
  const blossom = (a: number, size: number, col: string) => {
    const x = cx + Math.cos(a) * R, y = cy + Math.sin(a) * R;
    let p = '';
    for (let k = 0; k < 5; k++) {
      const t = (k / 5) * Math.PI * 2 + a;
      p += `<circle cx="${(x + Math.cos(t) * size * 0.62).toFixed(1)}" cy="${(y + Math.sin(t) * size * 0.62).toFixed(1)}" r="${(size * 0.55).toFixed(1)}" fill="${col}"/>`;
    }
    return p + `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${(size * 0.3).toFixed(1)}" fill="#E8C46A"/>`;
  };
  const deg = (d: number) => (d * Math.PI) / 180;
  const flowers = [
    blossom(deg(128), 20, flower), blossom(deg(142), 14, flower2), blossom(deg(114), 12, flower2), blossom(deg(155), 10, flower),
    blossom(deg(-38), 17, flower), blossom(deg(-52), 12, flower2), blossom(deg(-25), 10, flower2),
  ].join('');
  const berries = [100, 168, 176, -60, -18, 60].map((d) => {
    const a = deg(d);
    const x = cx + Math.cos(a) * (R + 16), y = cy + Math.sin(a) * (R + 16);
    return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" fill="${flower}" opacity=".8"/>`;
  }).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600" width="600" height="600"><circle cx="${cx}" cy="${cy}" r="${R}" fill="none" stroke="#7E9A76" stroke-width="2.2"/><circle cx="${cx}" cy="${cy}" r="${R - 5}" fill="none" stroke="#9DB394" stroke-width="1" stroke-dasharray="40 26"/>${leaves}${berries}${flowers}</svg>`;
  const uri = svgURI(svg);
  _wreath.set(key, uri);
  return uri;
}

/* ---------- Indigo design: textile band ---------- */
const _tile = new Map<string, string>();
export function textileCSS(fg: string, bg: string) {
  const key = fg + bg;
  const hit = _tile.get(key);
  if (hit) return hit;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="${fg}"/><g fill="none" stroke="${bg}" stroke-width="2"><circle cx="20" cy="20" r="13"/><circle cx="20" cy="20" r="7"/><circle cx="60" cy="60" r="13"/><circle cx="60" cy="60" r="7"/><path d="M47 7 L73 33 M73 7 L47 33"/><path d="M7 47 H33 M7 54 H33 M7 61 H33 M7 68 H33"/></g><g fill="${bg}"><circle cx="20" cy="20" r="2.4"/><circle cx="60" cy="60" r="2.4"/><circle cx="47" cy="20" r="2"/><circle cx="73" cy="20" r="2"/><circle cx="60" cy="7" r="2"/><circle cx="60" cy="33" r="2"/></g><path d="M40 0 V80 M0 40 H80" stroke="${bg}" stroke-opacity=".5" stroke-width="1.5"/><path d="M0 1 H80 M0 79 H80" stroke="${bg}" stroke-opacity=".35" stroke-width="2" stroke-dasharray="3 4"/></svg>`;
  const css = `url("${svgURI(svg)}")`;
  _tile.set(key, css);
  return css;
}

/* ---------- decorative backdrop behind the "View on Google Maps" card ---------- */
const _maps = new Map<string, string>();
export function mapArtURI(q: string, c: Colors) {
  const key = q + '|' + c.bg + c.ink + c.accent;
  const hit = _maps.get(key);
  if (hit) return hit;
  let seed = 0;
  for (let i = 0; i < q.length; i++) seed = (seed * 31 + q.charCodeAt(i)) % 233280;
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  const dark = isDark(c.bg);
  const land = mix(c.bg, dark ? '#ffffff' : c.ink, dark ? 0.1 : 0.04);
  const block = mix(c.bg, dark ? '#ffffff' : c.ink, dark ? 0.17 : 0.08);
  const street = mix(c.bg, '#ffffff', dark ? 0.2 : 0.75);
  const major = mix(c.bg, c.accent, dark ? 0.32 : 0.28);
  const water = mix(c.bg, '#5E93AE', dark ? 0.28 : 0.3);
  const park = mix(c.bg, '#6E9A6A', dark ? 0.24 : 0.26);
  const W = 1200, H = 600, ang = Math.round(rnd() * 40 - 20), gap = 46 + Math.round(rnd() * 22);
  let s = `<rect width="${W}" height="${H}" fill="${land}"/>`;
  let blocks = '';
  for (let x = -600; x < W + 600; x += gap) for (let y = -600; y < H + 600; y += gap) {
    const r = rnd();
    blocks += `<rect x="${x + 5}" y="${y + 5}" width="${gap - 10}" height="${gap - 10}" rx="3" fill="${r < 0.07 ? park : block}"/>`;
  }
  s += `<g transform="rotate(${ang} ${W / 2} ${H / 2})">${blocks}</g>`;
  if (rnd() < 0.5) {
    const y0 = H * (0.72 + rnd() * 0.12);
    s += `<path d="M0 ${y0} C${W * 0.25} ${y0 - 40} ${W * 0.55} ${y0 + 50} ${W} ${y0 - 20} V${H} H0Z" fill="${water}"/>`;
  } else {
    const x0 = W * (0.08 + rnd() * 0.18);
    s += `<path d="M${x0} -20 C${x0 + 120} ${H * 0.3} ${x0 - 80} ${H * 0.6} ${x0 + 60} ${H + 20}" stroke="${water}" stroke-width="${46 + rnd() * 30}" fill="none"/>`;
  }
  const r1 = ang + 90 + Math.round(rnd() * 30 - 15);
  s += `<g stroke="${street}" stroke-linecap="round" fill="none">
    <path d="M-100 ${H / 2 + 6} L${W + 100} ${H / 2 - 6}" stroke-width="16" transform="rotate(${ang} ${W / 2} ${H / 2})"/>
    <path d="M${W / 2 + 4} -100 L${W / 2 - 4} ${H + 100}" stroke-width="12" transform="rotate(${r1 - 90} ${W / 2} ${H / 2})"/>
  </g>
  <path d="M-50 ${H * 0.18} C${W * 0.3} ${H * 0.02} ${W * 0.7} ${H * 0.1} ${W + 50} ${H * 0.34}" stroke="${major}" stroke-width="14" fill="none" stroke-linecap="round"/>
  <path d="M-100 ${H / 2 + 6} L${W + 100} ${H / 2 - 6}" stroke="${major}" stroke-width="7" fill="none" transform="rotate(${ang} ${W / 2} ${H / 2})"/>`;
  const uri = svgURI(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">${s}</svg>`);
  _maps.set(key, uri);
  return uri;
}
